import React, { useState, useCallback, useRef } from 'react';
import { editImage, removeBackground, enhanceImageForPrinting } from '@/services/geminiService';
import { useCustomDesign } from '@/contexts/CustomDesignContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Loader2, Upload, Wand2, Download, Eraser, Sparkles, Save } from 'lucide-react';
import { toast } from 'sonner';

const LoadingSpinner: React.FC = () => (
  <div className="absolute inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-20 rounded-lg">
    <div className="flex flex-col items-center gap-3">
      <Loader2 className="h-12 w-12 animate-spin text-white" />
      <p className="text-white text-sm font-medium">Processing your image...</p>
    </div>
  </div>
);

const UploadIcon: React.FC = () => (
  <svg
    className="w-12 h-12 mb-4 text-gray-400"
    aria-hidden="true"
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 20 16"
  >
    <path
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2"
    />
  </svg>
);

export const ImageEditor: React.FC = () => {
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [editedImageUrl, setEditedImageUrl] = useState<string | null>(null);
  const [prompt, setPrompt] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { addDesign } = useCustomDesign();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File size must be less than 10MB');
        return;
      }

      setImageFile(file);
      setPreviewUrl(URL.createObjectURL(file));
      setEditedImageUrl(null);
      toast.success('Image uploaded successfully!');
    }
  };

  const handleEditImage = useCallback(async () => {
    if (!imageFile || !prompt.trim()) {
      toast.error('Please upload an image and provide editing instructions');
      return;
    }

    setIsLoading(true);
    try {
      const resultUrl = await editImage(prompt, imageFile);
      setEditedImageUrl(resultUrl);
      toast.success('Image edited successfully!');
    } catch (error) {
      console.error('Image editing error:', error);
      toast.error('Failed to edit image. Please check your API key and try again.');
    } finally {
      setIsLoading(false);
    }
  }, [imageFile, prompt]);

  const handleRemoveBackground = useCallback(async () => {
    if (!imageFile) {
      toast.error('Please upload an image first');
      return;
    }

    setIsLoading(true);
    try {
      const resultUrl = await removeBackground(imageFile);
      setEditedImageUrl(resultUrl);
      toast.success('Background removed successfully!');
    } catch (error) {
      console.error('Background removal error:', error);
      toast.error('Failed to remove background. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [imageFile]);

  const handleEnhanceImage = useCallback(async () => {
    if (!imageFile) {
      toast.error('Please upload an image first');
      return;
    }

    setIsLoading(true);
    try {
      const resultUrl = await enhanceImageForPrinting(imageFile);
      setEditedImageUrl(resultUrl);
      toast.success('Image enhanced for printing!');
    } catch (error) {
      console.error('Image enhancement error:', error);
      toast.error('Failed to enhance image. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [imageFile]);

  const handleSaveDesign = useCallback(() => {
    if (!editedImageUrl) {
      toast.error('No edited design to save');
      return;
    }

    addDesign({
      imageUrl: editedImageUrl,
      prompt: prompt || 'Edited image',
    });

    toast.success('Design saved! You can now apply it to products.');
  }, [editedImageUrl, prompt, addDesign]);

  const handleDownload = useCallback((imageUrl: string, prefix: string) => {
    const link = document.createElement('a');
    link.href = imageUrl;
    link.download = `${prefix}-${Date.now()}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Image downloaded!');
  }, []);

  const triggerFileSelect = () => fileInputRef.current?.click();

  return (
    <div className="container mx-auto p-6 space-y-8">
      <div className="text-center space-y-2">
        <h2 className="text-4xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
          AI Image Editor
        </h2>
        <p className="text-muted-foreground max-w-2xl mx-auto">
          Transform your images with AI-powered editing tools
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Editor Panel */}
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Wand2 className="h-5 w-5" />
              Edit Your Image
            </CardTitle>
            <CardDescription>
              Upload an image and choose an editing option
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* File Upload */}
            <div className="space-y-2">
              <label className="text-sm font-medium flex items-center gap-2">
                <Upload className="h-4 w-4" />
                Upload Image
              </label>
              <div
                className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:border-primary transition-colors"
                onClick={triggerFileSelect}
              >
                {previewUrl ? (
                  <div className="space-y-2">
                    <img
                      src={previewUrl}
                      alt="Preview"
                      className="max-h-32 mx-auto rounded"
                    />
                    <p className="text-sm text-muted-foreground">
                      {imageFile?.name}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Click to change
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <UploadIcon />
                    <p className="text-sm text-muted-foreground">
                      Click to upload or drag and drop
                    </p>
                    <p className="text-xs text-muted-foreground">
                      PNG, JPG up to 10MB
                    </p>
                  </div>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                onChange={handleFileChange}
                accept="image/*"
              />
            </div>

            {/* Editing Options */}
            <Tabs defaultValue="custom" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="custom">Custom</TabsTrigger>
                <TabsTrigger value="quick">Quick Edit</TabsTrigger>
                <TabsTrigger value="enhance">Enhance</TabsTrigger>
              </TabsList>

              <TabsContent value="custom" className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">
                    Describe Your Edit
                  </label>
                  <Input
                    type="text"
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    placeholder='e.g., "Add a retro filter" or "Make it brighter"'
                    disabled={!imageFile}
                  />
                </div>
                <Button
                  onClick={handleEditImage}
                  disabled={isLoading || !imageFile || !prompt.trim()}
                  className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                  size="lg"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Wand2 className="mr-2 h-4 w-4" />
                      Apply Custom Edit
                    </>
                  )}
                </Button>
              </TabsContent>

              <TabsContent value="quick" className="space-y-3">
                <Button
                  onClick={handleRemoveBackground}
                  disabled={isLoading || !imageFile}
                  variant="outline"
                  className="w-full"
                  size="lg"
                >
                  <Eraser className="mr-2 h-4 w-4" />
                  Remove Background
                </Button>
                <p className="text-xs text-muted-foreground text-center">
                  Perfect for creating transparent logo designs
                </p>
              </TabsContent>

              <TabsContent value="enhance" className="space-y-3">
                <Button
                  onClick={handleEnhanceImage}
                  disabled={isLoading || !imageFile}
                  variant="outline"
                  className="w-full"
                  size="lg"
                >
                  <Sparkles className="mr-2 h-4 w-4" />
                  Enhance for Printing
                </Button>
                <p className="text-xs text-muted-foreground text-center">
                  Optimize image quality for high-resolution printing
                </p>
              </TabsContent>
            </Tabs>

            {/* Action Buttons */}
            {editedImageUrl && (
              <div className="flex gap-3 pt-4 border-t">
                <Button
                  onClick={handleSaveDesign}
                  variant="default"
                  className="flex-1"
                >
                  <Save className="mr-2 h-4 w-4" />
                  Save Design
                </Button>
                <Button
                  onClick={() => handleDownload(editedImageUrl, 'edited')}
                  variant="outline"
                  className="flex-1"
                >
                  <Download className="mr-2 h-4 w-4" />
                  Download
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Preview Panel */}
        <div className="space-y-4">
          {/* Original Image */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Original</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="relative aspect-square bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center">
                {previewUrl ? (
                  <img
                    src={previewUrl}
                    alt="Original"
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <p className="text-sm text-muted-foreground">
                    Upload an image to get started
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Edited Image */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Edited Result</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="relative aspect-square bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center">
                {isLoading && <LoadingSpinner />}
                {editedImageUrl ? (
                  <img
                    src={editedImageUrl}
                    alt="Edited"
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <p className="text-sm text-muted-foreground">
                    Your edited image will appear here
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};